/*
 * CAPTURE-OWNED MODULE
 * Similar-field suggestions for Template Studio's Add Field form, built on the
 * generic shared-word matcher (utils/fieldMatch).
 *
 * The Field Library is ONE catalog (decision 10): matching never depends on
 * the template's document type. The type only decides how a match is used:
 *   - linked to the template's type (an iim_field_associations row exists), or
 *     the template has no type yet: "Use library field" adds it directly;
 *   - linked only to other types (needsLink): "Link to <type> and use" first
 *     links the field to the template's type (POST .../associations), then
 *     adds it, so any suggested standard field is one click away (A2).
 */
import { matchNames } from './fieldMatch';
import { isDocumentAttributeKey } from './documentAttributes';

/**
 * Merge the per-association rows GET /templates/field-library returns (one
 * row per field x document type) into one item per field_key. Inactive rows
 * and document attributes are left out.
 * @returns {Array<{ key, label, value_type, docTypes: string[], rows: object[] }>}
 */
export function buildLibraryCatalog(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row || !row.field_key) continue;
    if (row.is_active === false) continue;
    if (isDocumentAttributeKey(row.field_key)) continue;
    let item = byKey.get(row.field_key);
    if (!item) {
      item = { key: row.field_key, label: row.label || row.field_key, value_type: row.value_type, docTypes: [], rows: [] };
      byKey.set(row.field_key, item);
    }
    item.rows.push(row);
    if (row.doc_type && !item.docTypes.includes(row.doc_type)) item.docTypes.push(row.doc_type);
  }
  for (const item of byKey.values()) item.docTypes.sort();
  return [...byKey.values()];
}

/**
 * @param {object}  args
 * @param {string}  args.label          what the user typed as the label
 * @param {string}  [args.fieldKey]     the key box (derived or typed)
 * @param {Array}   args.catalog        buildLibraryCatalog(...)
 * @param {Array}   args.templateFields the template's own fields
 * @param {string}  [args.documentType] the template's document type key, if any
 * @returns {{
 *   attribute: boolean,
 *   templateDuplicates: Array<{ field, tier }>,
 *   library: Array<{ item, tier, linked, usedBy: string[], templateState: 'available'|'on_template'|'on_template_disabled', templateField, row }>
 * }}
 */
export function findFieldSuggestions({ label, fieldKey, catalog, templateFields, documentType }) {
  const typed = { label: label || '', key: fieldKey || '' };
  const empty = { attribute: false, templateDuplicates: [], library: [] };
  if (!String(typed.label).trim() && !String(typed.key).trim()) return empty;
  if (isDocumentAttributeKey(typed.key) || isDocumentAttributeKey(String(typed.label).trim().replace(/\s+/g, '_'))) {
    return { ...empty, attribute: true };
  }

  const own = (templateFields || []).filter((f) => f && !isDocumentAttributeKey(f.field_key));

  // Same-template duplicates: exact only. Contained/partial template fields
  // are ordinary neighbours ("Date" on a template with Invoice Date), not
  // duplicates.
  const templateDuplicates = matchNames(typed, own.map((f) => ({ key: f.field_key, label: f.label, field: f })))
    .filter((m) => m.tier === 'exact')
    .map((m) => ({ field: m.item.field, tier: m.tier }));

  // Library: the catalog only. Template fields are never library candidates.
  const byKey = new Map(own.map((f) => [f.field_key, f]));
  const library = matchNames(typed, catalog || []).map((m) => {
    const item = m.item;
    const templateField = byKey.get(item.key) || null;
    const linked = !documentType || item.docTypes.includes(documentType);
    // A typed template whose type the field is not linked to: using it means
    // linking it first. A template with no type never links.
    const needsLink = !linked;
    const row = (documentType && item.rows.find((r) => r.doc_type === documentType)) || item.rows[0];
    return {
      item,
      tier: m.tier,
      linked,
      needsLink,
      usedBy: item.docTypes,
      templateState: templateField ? (templateField.enabled === false ? 'on_template_disabled' : 'on_template') : 'available',
      templateField,
      row,
    };
  });

  return { attribute: false, templateDuplicates, library };
}

/**
 * True when Save should ask once more before creating: a same-template
 * duplicate, or an exact library match, linked or not (every match can be
 * used: an unlinked one is linked first).
 */
export function needsSecondPress(suggestions) {
  if (!suggestions || suggestions.attribute) return false;
  return suggestions.templateDuplicates.length > 0
    || suggestions.library.some((s) => s.tier === 'exact');
}

/** "correspondence" -> "Correspondence", "purchase_order" -> "Purchase order". */
export function docTypeName(key) {
  const s = String(key || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}
